import { reatomAsync, withAbort } from "@reatom/async";
import { action, atom, type Ctx } from "@reatom/framework";
import { ProductSelectionDto } from "definitions/product-storage/models/product-selection-dto";
import { UpdateProductSelectionDto } from "definitions/product-storage/models/update-product-selection-dto";
import { API } from "src/api";
import { withDebounce, withIsLoadingAtom } from "src/common/pipes";
import { productSelectionStore } from "src/store/atoms";
import { isCanceledRequestError } from "src/util";

const SELECTION_SAVE_DELAY = 1000;

type SelectionSaveResult =
  | { status: "saved"; savedSample: ProductSelectionDto }
  | {
      status: "failed";
      error: unknown;
      updateData: UpdateProductSelectionDto;
    };

const pendingSelectionUpdatesAtom = atom(
  new Map<number, UpdateProductSelectionDto>(),
  "pendingSelectionUpdates",
);

export const updateProductSelectionRequest = reatomAsync(
  async (ctx, selectionId: number, updateData: UpdateProductSelectionDto) => {
    return API.productSamplesApi
      .productSelectionControllerUpdate(
        { selectionId, updateProductSelectionDto: updateData },
        {
          signal: ctx.controller.signal,
        },
      )
      .then((response) => response.data);
  },
  "updateProductSelectionRequest",
).pipe(withAbort(), withIsLoadingAtom());

const putPendingUpdate = (
  ctx: Ctx,
  selectionId: number,
  updateData: UpdateProductSelectionDto,
) => {
  const pending = new Map(ctx.get(pendingSelectionUpdatesAtom));

  pending.set(selectionId, updateData);
  pendingSelectionUpdatesAtom(ctx, pending);
};

const restorePendingUpdate = (
  ctx: Ctx,
  selectionId: number,
  updateData: UpdateProductSelectionDto,
) => {
  if (ctx.get(pendingSelectionUpdatesAtom).has(selectionId)) {
    return;
  }

  putPendingUpdate(ctx, selectionId, updateData);
};

const takeNextPendingUpdate = (ctx: Ctx) => {
  const pending = ctx.get(pendingSelectionUpdatesAtom);
  const [next] = Array.from(pending);

  if (!next) {
    return null;
  }

  const [selectionId, updateData] = next;
  const rest = new Map(pending);

  rest.delete(selectionId);
  pendingSelectionUpdatesAtom(ctx, rest);

  return { selectionId, updateData };
};

const applySavedSelection = (ctx: Ctx, savedSample: ProductSelectionDto) => {
  const openSample = ctx.get(productSelectionStore.currentSelectionAtom);

  if (openSample?.id === savedSample.id) {
    productSelectionStore.currentSelectionAtom(ctx, savedSample);
  }
};

const sendPendingSelectionUpdates = action(async (ctx) => {
  const results = new Map<number, SelectionSaveResult>();
  let next = takeNextPendingUpdate(ctx);

  while (next) {
    const { selectionId, updateData } = next;

    try {
      const savedSample = await ctx.schedule(() =>
        updateProductSelectionRequest(ctx, selectionId, updateData),
      );

      applySavedSelection(ctx, savedSample);
      results.set(selectionId, { status: "saved", savedSample });
    } catch (error) {
      results.set(selectionId, { status: "failed", error, updateData });

      if (isCanceledRequestError(error)) {
        restorePendingUpdate(ctx, selectionId, updateData);
        break;
      }
    }

    next = takeNextPendingUpdate(ctx);
  }

  return results;
}, "sendPendingSelectionUpdates");

const debouncedSendPendingSelectionUpdates = sendPendingSelectionUpdates.pipe(
  withDebounce(SELECTION_SAVE_DELAY),
);

export const saveProductSelectionRequest = action(
  async (ctx, selectionId: number, updateData: UpdateProductSelectionDto) => {
    putPendingUpdate(ctx, selectionId, updateData);

    const results = await ctx.schedule(() =>
      debouncedSendPendingSelectionUpdates(ctx),
    );
    const result = results.get(selectionId);

    if (result?.status === "failed") {
      throw result.error;
    }

    return result?.status === "saved" ? result.savedSample : null;
  },
  "saveProductSelectionRequest",
);

export const flushProductSelectionRequest = action(async (ctx) => {
  if (ctx.get(pendingSelectionUpdatesAtom).size === 0) {
    return;
  }

  const results = await ctx
    .schedule(() => sendPendingSelectionUpdates(ctx))
    .catch(() => null);

  results?.forEach((result, selectionId) => {
    if (result.status === "failed") {
      restorePendingUpdate(ctx, selectionId, result.updateData);
    }
  });
}, "flushProductSelectionRequest");
